/**
 * "Downloads" for one version — the two installer disk images, managed in one
 * place.
 *
 * THE SHAPE OF THIS DIALOG IS THE PERMISSION MODEL. Fixed rows, always both,
 * in the same order, whether or not anything is attached yet: the kinds are a
 * closed set (`ReleaseArtifact::KINDS` upstream), so an empty row is "not
 * published yet", never "someone forgot a row". Each row states its own
 * audience in words.
 *
 * A THIRD ROW, "Release Notes", was here until 2026-09-16 — the one kind that
 * could be UPLOADED. It is gone: the release notes are the CHANGE LOG, written
 * on the release itself and rendered on the page, and a second place to attach
 * them was a second place to forget to update them.
 *
 * DISK IMAGES ARE LINKED, NEVER UPLOADED. That is not a UI preference —
 * portal-api refuses an uploaded image, because an installer image is multiple
 * gigabytes and neither this proxy nor Rails will carry those bytes. The
 * dialog says so rather than letting the operator find out from a validation
 * error.
 *
 * Downloading from here goes through the SAME audited endpoint every other
 * portal uses, so a staff member without `releases:download_images` is refused
 * — and recorded — exactly as a customer would be. The button is not hidden
 * from them: a refusal that leaves a log entry is more useful than a control
 * that quietly isn't there.
 */
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link as MuiLink,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Paperclip, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { JSX } from "react";

import { ConfirmDialog } from "../../../components/ui/confirm-dialog.js";
import { PageLoader } from "@/components/ui/page-loader";
import { ApiError } from "../../../lib/api.js";
import {
  ARTIFACT_AUDIENCE,
  ARTIFACT_KINDS,
  ARTIFACT_LABELS,
  type ArtifactKind,
  type ReleaseArtifactDTO,
  deleteArtifact,
  formatBytes,
  listArtifacts,
  requestDownload,
  saveArtifact,
  startDownload,
  updateArtifact,
} from "./artifacts-api.js";

interface Props {
  releaseId: number;
  version: string;
  open: boolean;
  onClose: () => void;
}

/** Per-row draft state. Kept per KIND rather than per artifact id so a row
 *  that has nothing attached yet is still editable. */
interface RowDraft {
  external_url: string;
  checksum: string;
  label: string;
}

const EMPTY_DRAFT: RowDraft = { external_url: "", checksum: "", label: "" };

export function ReleaseArtifactsDialog({ releaseId, version, open, onClose }: Props): JSX.Element {
  const qc = useQueryClient();
  const [drafts, setDrafts] = useState<Record<string, RowDraft>>({});
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ArtifactKind | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["admin", "release-artifacts", releaseId],
    queryFn: () => listArtifacts(releaseId),
    enabled: open,
  });

  const byKind = useMemo(() => {
    const map = new Map<string, ReleaseArtifactDTO>();
    for (const a of data ?? []) map.set(a.kind, a);
    return map;
  }, [data]);

  // Seed each row's draft from whatever is stored, once the data lands.
  useEffect(() => {
    if (!data) return;
    const next: Record<string, RowDraft> = {};
    for (const kind of ARTIFACT_KINDS) {
      const a = byKind.get(kind);
      next[kind] = {
        external_url: a?.external_url ?? "",
        checksum: a?.checksum ?? "",
        label: a?.label ?? "",
      };
    }
    setDrafts(next);
  }, [data, byKind]);

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ["admin", "release-artifacts", releaseId] });
    void qc.invalidateQueries({ queryKey: ["admin", "release-downloads"] });
  };

  const draftFor = (kind: ArtifactKind): RowDraft => drafts[kind] ?? EMPTY_DRAFT;

  const setDraft = (kind: ArtifactKind, patch: Partial<RowDraft>): void => {
    setDrafts((d) => ({ ...d, [kind]: { ...(d[kind] ?? EMPTY_DRAFT), ...patch } }));
  };

  const fail = (kind: string, e: unknown): void => {
    setRowError((r) => ({
      ...r,
      [kind]: e instanceof ApiError && e.message ? e.message : "That didn't work.",
    }));
  };

  const clearError = (kind: string): void =>
    setRowError((r) => {
      const { [kind]: _removed, ...rest } = r;
      return rest;
    });

  const saveMut = useMutation({
    mutationFn: async (kind: ArtifactKind) => {
      const draft = draftFor(kind);
      await saveArtifact(releaseId, {
        kind,
        external_url: draft.external_url.trim(),
        ...(draft.checksum.trim() ? { checksum: draft.checksum.trim() } : {}),
        ...(draft.label.trim() ? { label: draft.label.trim() } : {}),
      });
    },
    onSuccess: (_r, kind) => {
      clearError(kind);
      setNotice(`${ARTIFACT_LABELS[kind]} saved.`);
      invalidate();
    },
    onError: (e, kind) => fail(kind, e),
  });

  const retireMut = useMutation({
    mutationFn: ({ kind, active }: { kind: ArtifactKind; active: boolean }) =>
      updateArtifact(releaseId, kind, { active }),
    onSuccess: () => invalidate(),
    onError: (e, vars) => fail(vars.kind, e),
  });

  // A download is a REQUEST, not a link. The endpoint decides, records the
  // attempt either way, and hands back a short-lived address we navigate to.
  const downloadMut = useMutation({
    mutationFn: (kind: ArtifactKind) => requestDownload(releaseId, kind),
    onSuccess: (ticket, kind) => {
      clearError(kind);
      setNotice(`${ARTIFACT_LABELS[kind]} — download starting.`);
      invalidate();
      startDownload(ticket.url, ticket.filename);
    },
    onError: (e, kind) => fail(kind, e),
  });

  async function confirmDelete(): Promise<void> {
    if (!pendingDelete) return;
    try {
      await deleteArtifact(releaseId, pendingDelete);
    } catch {
      throw new Error("delete-failed");
    }
    setPendingDelete(null);
    invalidate();
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>Downloads for release {version}</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 2 }}>
            The two disk images are restricted to{" "}
            <strong>resellers, sales and super admins</strong>, and each is recorded as a link to
            where it is hosted. Every download attempt is recorded, granted or refused. What
            changed in this version is its <strong>change log</strong>, written on the version
            itself — it is not a file attached here.
          </Typography>

          {notice && (
            <Alert severity="success" onClose={() => setNotice(null)} sx={{ mb: 2, fontSize: 13 }}>
              {notice}
            </Alert>
          )}

          {isLoading ? (
            <PageLoader />
          ) : isError ? (
            <Alert
              severity="error"
              sx={{ fontSize: 13 }}
              action={
                <Button size="small" onClick={() => void refetch()}>
                  Retry
                </Button>
              }
            >
              {error instanceof ApiError && error.message
                ? error.message
                : "Could not load this release's downloads."}
            </Alert>
          ) : (
            <Stack spacing={2.5}>
              {ARTIFACT_KINDS.map((kind) => {
                const artifact = byKind.get(kind);
                const draft = draftFor(kind);

                return (
                  <Box
                    key={kind}
                    // A stable hook per row. The kind labels ("Disk 1 Image")
                    // also appear in the Releases panel behind this modal, so a
                    // text-based selector picks the hidden one — the same
                    // nav-name clash that has bitten other specs here.
                    data-testid={`artifact-row-${kind}`}
                    sx={{
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 2,
                      opacity: artifact && !artifact.active ? 0.6 : 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 2,
                        mb: 1.5,
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                          {ARTIFACT_LABELS[kind]}
                          {artifact && !artifact.active && (
                            <Chip label="Retired" size="small" sx={{ ml: 1, height: 20 }} />
                          )}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
                          {ARTIFACT_AUDIENCE[kind]}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {artifact && (
                          <>
                            <Tooltip title="Download (recorded)">
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label={`Download ${ARTIFACT_LABELS[kind]}`}
                                  disabled={downloadMut.isPending}
                                  onClick={() => downloadMut.mutate(kind)}
                                >
                                  <Download size={16} />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Button
                              size="small"
                              variant="text"
                              color="inherit"
                              onClick={() =>
                                retireMut.mutate({ kind, active: !artifact.active })
                              }
                            >
                              {artifact.active ? "Retire" : "Restore"}
                            </Button>
                            <Tooltip title="Remove — the download log is kept">
                              <IconButton
                                size="small"
                                aria-label={`Remove ${ARTIFACT_LABELS[kind]}`}
                                sx={{ color: "error.main" }}
                                onClick={() => setPendingDelete(kind)}
                              >
                                <Trash2 size={16} />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </Box>

                    {artifact ? (
                      <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1.5 }}>
                        <Paperclip size={12} style={{ verticalAlign: "-1px", marginRight: 4 }} />
                        {artifact.filename ?? "Attached"} · {formatBytes(artifact.byte_size)} ·{" "}
                        {artifact.download_count} download
                        {artifact.download_count === 1 ? "" : "s"}
                        {artifact.denied_count > 0 && (
                          <Box component="span" sx={{ color: "warning.main" }}>
                            {" "}
                            · {artifact.denied_count} refused
                          </Box>
                        )}
                      </Typography>
                    ) : (
                      <Typography sx={{ fontSize: 12, color: "text.disabled", mb: 1.5 }}>
                        Nothing published yet.
                      </Typography>
                    )}

                    <Box sx={{ display: "grid", gap: 1.5 }}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Download link"
                        placeholder="https://downloads.xopnetworks.com/…"
                        value={draft.external_url}
                        onChange={(e) => setDraft(kind, { external_url: e.target.value })}
                        helperText="Disk images are recorded as a link — they are far too large to upload through this box."
                        FormHelperTextProps={{ sx: { fontSize: 11 } }}
                      />
                      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                        <TextField
                          size="small"
                          label="Checksum"
                          placeholder="sha256:…"
                          value={draft.checksum}
                          onChange={(e) => setDraft(kind, { checksum: e.target.value })}
                          sx={{ flex: "1 1 220px" }}
                        />
                        <TextField
                          size="small"
                          label="Label (optional)"
                          value={draft.label}
                          onChange={(e) => setDraft(kind, { label: e.target.value })}
                          sx={{ flex: "1 1 200px" }}
                        />
                      </Box>

                      {rowError[kind] && (
                        <Typography sx={{ fontSize: 12, color: "error.main" }}>
                          {rowError[kind]}
                        </Typography>
                      )}

                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={saveMut.isPending || !draft.external_url.trim()}
                          onClick={() => saveMut.mutate(kind)}
                        >
                          Save link
                        </Button>
                        {artifact?.external_url && (
                          <MuiLink
                            href={artifact.external_url}
                            target="_blank"
                            rel="noreferrer"
                            sx={{ fontSize: 12, ml: "auto" }}
                          >
                            Open link
                          </MuiLink>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="text" color="inherit" onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title="Remove this download?"
        description={
          <>
            <strong>{pendingDelete ? ARTIFACT_LABELS[pendingDelete] : ""}</strong> will be removed
            from release {version}. The record of who downloaded it is kept.
          </>
        }
        confirmLabel="Remove"
        onConfirm={confirmDelete}
      />
    </>
  );
}
